import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import moment from 'moment'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import API from '../../../../services/API'
import SmallModal from '../../../../components/Modals/SmallModal'
import RiskScores from './RiskScores'
import ConsequenceSelector from '../ConsequenceSelector'
import LikelihoodSelector from '../LikelihoodSelector'
import CqcLinkageFields from '../CqcLinkageFields'
import {
  CQC_ACTIVITY_LABEL,
  keyQuestionForStatement,
  statementTitle,
  useCqcStatements,
} from '../cqcLinkage'
import { getRAGband } from '../getRAGcolor'
import { isReviewOverdue } from '../getRows'
import { GlobalContext } from '../../../../App'
import EmployeeSelector from '../../../events/EmployeeSelector'
import { useDocumentTitle } from '../../../../helpers/DocumentTitleManager'

const sectionMotion = (index) => ({
  component: motion.div,
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay: 0.08 * index, ease: 'easeOut' },
})

// Frosted-glass hero button (same treatment as the dashboard heroes)
const heroButtonSx = {
  color: '#FFFFFF',
  borderColor: 'rgba(255, 255, 255, 0.4)',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(8px)',
  '&:hover': {
    borderColor: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.16)',
  },
}

const glassChipSx = {
  color: '#FFFFFF',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  backdropFilter: 'blur(8px)',
  borderRadius: 1,
  fontWeight: 600,
}

const SectionLabel = ({ children }) => (
  <Typography
    variant="smallGrey"
    sx={{
      textTransform: 'uppercase',
      letterSpacing: '0.14em',
      fontWeight: 600,
      fontSize: '0.72rem',
    }}
  >
    {children}
  </Typography>
)

const COMMENT_PAGE_SIZE = 20

const SingleRisk = () => {
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const { id: riskID } = useParams()
  const {setPageTitle} = useDocumentTitle()
  const [risk, setRisk] = useState({})
  const [comments, setComments] = useState([])
  const [currentRatingLikelihood, setCurrentRatingLikelihood] = useState('')
  const [currentRatingConsequence, setCurrentRatingConsequence] = useState('')
  const navigate = useNavigate()
  const [newComment, setNewComment] = useState('')
  const [markReviewed, setMarkReviewed] = useState(true)
  const [visibleComments, setVisibleComments] = useState(COMMENT_PAGE_SIZE)
  const [openDeletePopup, setOpenDeletePopup] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const { statements, keyQuestions } = useCqcStatements()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [mitigation, setMitigation] = useState('')
  const [actions, setActions] = useState('')
  const [cqcActivityType, setCqcActivityType] = useState(null)
  const [cqcQualityStatementKeys, setCqcQualityStatementKeys] = useState([])

  useEffect(() => {
    if (risk?.riskID) {
      setPageTitle(`Risk #${risk.riskID}`)
    }
  }, [risk?.riskID, setPageTitle])

  const applyRisk = (data) => {
    setComments(data.comments || [])
    const { comments: _comments, ...rest } = data
    setRisk(rest)
    setTitle(rest?.title ?? '')
    setDescription(rest?.description || '')
    setMitigation(rest?.mitigation || '')
    setActions(rest?.actions || '')
    setCqcActivityType(rest?.cqcActivityType ?? null)
    setCqcQualityStatementKeys(rest?.cqcQualityStatementKeys ?? [])
    setCurrentRatingConsequence(
      rest.currentRatingConsequence ?? rest.initialRatingConsequence ?? '',
    )
    setCurrentRatingLikelihood(
      rest.currentRatingLikelihood ?? rest.initialRatingLikelihood ?? '',
    )
  }

  const fetchRisk = () =>
    API.get(`/riskregister/${riskID}`)
      .then((res) => {
        if (res.status === 200) applyRisk(res.data)
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))

  useEffect(() => {
    setVisibleComments(COMMENT_PAGE_SIZE)
    fetchRisk()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskID])

  const handleDeleteRisk = () => {
    API.post('/riskregister/delete', { riskID })
      .then((res) => {
        if (res.status === 200) {
          navigate('/risk-register')
          setToast({ message: 'Risk Deleted' })
        } else {
          setToast({
            message: 'Unable to delete Risk. Please try again later.',
          })
        }
      })
      .catch(() =>
        setToast({ message: 'Unable to delete Risk. Please try again later.' }),
      )
  }

  const handleSubmitComment = (e) => {
    e.preventDefault()
    API.post('/riskregistercomment', {
      comment: newComment,
      risk: risk._id,
      currentRatingConsequence,
      currentRatingLikelihood,
      markedAsReviewed: markReviewed,
    })
      .then((res) => {
        if (res.status === 200) {
          setNewComment('')
          setMarkReviewed(true)
          setToast({
            message: markReviewed
              ? 'Update posted - review date updated'
              : 'Update posted',
          })
          fetchRisk()
        }
      })
      .catch((e) => console.log(e))
  }

  const handleUpdate = (e) => {
    e?.preventDefault()
    API.put('/riskregister/' + risk._id, {
      title,
      description,
      mitigation,
      actions,
      riskOwner: risk?.riskOwner?._id,
      cqcActivityType,
      cqcQualityStatementKeys,
    })
      .then((res) => {
        if (res.status === 200) {
          setEditMode(false)
          setToast({ message: 'Changes Saved' })
          fetchRisk()
        }
      })
      .catch((e) => console.log(e))
  }

  const handleCancelEdit = () => {
    applyRisk({ ...risk, comments })
    setEditMode(false)
  }

  // Saves immediately - assigning an owner shouldn't require entering edit
  // mode. Updates local state rather than refetching so any in-progress
  // edit-mode changes aren't discarded.
  const handleAssignOwner = (value) => {
    API.put('/riskregister/' + risk._id, { riskOwner: value?._id ?? null })
      .then((res) => {
        if (res.status === 200) {
          setRisk((prev) => ({ ...prev, riskOwner: value ?? null }))
          setToast({
            message: value
              ? `Risk assigned to ${value.firstName} ${value.lastName}`
              : 'Risk owner removed',
          })
        }
      })
      .catch((e) => console.log(e))
  }

  if (loading) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          Loading risk…
        </Typography>
      </Box>
    )
  }

  const currentRating =
    (risk.currentRatingConsequence || 0) *
      (risk.currentRatingLikelihood || 0) ||
    (risk.initialRatingConsequence || 0) * (risk.initialRatingLikelihood || 0)
  const band = getRAGband(currentRating)
  const overdue = isReviewOverdue(risk.lastReview, risk.createdAt)
  const linkedKeyQuestions = [
    ...new Set(
      (risk.cqcQualityStatementKeys ?? [])
        .map((key) => keyQuestionForStatement(key, statements))
        .filter(Boolean),
    ),
  ]
  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.created) - new Date(a.created),
  )

  return (
    <Stack gap={2}>
      {/* Hero */}
      <Box
        {...sectionMotion(0)}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          px: { xs: 3, md: 4 },
          py: { xs: 3, md: 3.5 },
          color: '#FFFFFF',
          background:
            'linear-gradient(120deg, #1A2230 0%, #242F40 55%, #1E3A45 100%)',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'radial-gradient(600px 300px at 85% -20%, rgba(35, 154, 164, 0.35), transparent 70%),' +
              'radial-gradient(500px 260px at -10% 110%, rgba(204, 164, 59, 0.16), transparent 70%),' +
              'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
            backgroundSize: 'auto, auto, 22px 22px',
          }}
        />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          gap={2}
          sx={{ position: 'relative' }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Typography
              sx={{
                color: '#4DBBC4',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontSize: '0.78rem',
                fontWeight: 600,
              }}
            >
              Risk Register &middot; ID {risk.riskID ?? ''}
            </Typography>
            {editMode ? (
              <TextField
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Risk Title"
                size="small"
                autoComplete="off"
                sx={{
                  mt: 1,
                  maxWidth: 560,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                    borderRadius: 1,
                  },
                }}
              />
            ) : (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mt: 0.5,
                  overflowWrap: 'anywhere',
                  color: 'white',
                }}
              >
                {title || 'Untitled Risk'}
              </Typography>
            )}
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
              {risk.category && (
                <Chip size="small" label={risk.category} sx={glassChipSx} />
              )}
              {risk.cqcActivityType && (
                <Chip
                  size="small"
                  label={CQC_ACTIVITY_LABEL[risk.cqcActivityType]}
                  sx={glassChipSx}
                />
              )}
              <Chip
                size="small"
                label={`${band.label} risk`}
                sx={{
                  borderRadius: 1,
                  fontWeight: 700,
                  color: '#FFFFFF',
                  backgroundColor: band.color,
                }}
              />
              {overdue && (
                <Chip
                  size="small"
                  label="Review overdue"
                  sx={{
                    borderRadius: 1,
                    fontWeight: 700,
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(227, 37, 37, 0.85)',
                  }}
                />
              )}
            </Stack>
            <Typography sx={{ mt: 1.5, fontSize: '0.78rem', opacity: 0.7, color: 'white' }}>
              {risk.lastReview
                ? `Last reviewed ${moment(risk.lastReview).format('Do MMM YYYY')} (${moment(risk.lastReview).fromNow()})`
                : 'Never reviewed'}
              {' · '}
              {risk.riskOwner
                ? `Owned by ${risk.riskOwner.firstName} ${risk.riskOwner.lastName}`
                : 'No owner assigned'}
            </Typography>
          </Box>

          <Stack alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={92}
                thickness={4}
                sx={{ color: 'rgba(255, 255, 255, 0.15)' }}
                aria-hidden="true"
              />
              <CircularProgress
                variant="determinate"
                value={Math.min((currentRating / 25) * 100, 100)}
                size={92}
                thickness={4}
                sx={{ color: band.color, position: 'absolute', left: 0 }}
                aria-label={`Current risk score ${currentRating} out of 25`}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.6rem',
                    lineHeight: 1,
                    color: 'white',
                  }}
                >
                  {currentRating || '—'}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.6rem', opacity: 0.7, color: 'white' }}
                >
                  / 25
                </Typography>
              </Box>
            </Box>
            <Stack
              direction="row"
              gap={1}
              flexWrap="wrap"
              justifyContent="center"
            >
              {editMode ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={handleUpdate}
                    sx={heroButtonSx}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<HighlightOffIcon />}
                    onClick={handleCancelEdit}
                    sx={heroButtonSx}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                    sx={heroButtonSx}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setOpenDeletePopup(true)}
                    sx={{
                      ...heroButtonSx,
                      color: '#FF8A8A',
                      borderColor: 'rgba(255, 138, 138, 0.5)',
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <SmallModal
        open={!!openDeletePopup}
        onClose={() => setOpenDeletePopup(false)}
      >
        <Stack gap={1.5} alignItems="center">
          <Typography variant="h6" color="error">
            Delete this Risk?
          </Typography>
          <Typography textAlign="center" color="text.secondary">
            This action cannot be undone.
          </Typography>
          <Button
            onClick={() => setOpenDeletePopup(false)}
            variant="outlined"
            color="primary"
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRisk}
            variant="contained"
            color="error"
            fullWidth
          >
            I’m sure — delete this risk
          </Button>
        </Stack>
      </SmallModal>

      {/* Score journey */}
      <Paper variant="outlined" sx={{ p: 2.5 }} {...sectionMotion(1)}>
        <SectionLabel>Score journey</SectionLabel>
        <Box sx={{ mt: 1.5 }}>
          <RiskScores risk={risk} />
        </Box>
      </Paper>
      <Grid container spacing={2} {...sectionMotion(2)}>
        {/* Left: Details */}
        <Grid item xs={12} md={7} display="flex">
          <Paper variant="outlined" sx={{ p: 2.5, width: '100%' }}>
            <Stack
              gap={2}
              component={editMode ? 'form' : 'div'}
              onSubmit={handleUpdate}
            >
              <Box>
                <SectionLabel>Risk Description</SectionLabel>
                {editMode ? (
                  <TextField
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    sx={{ mt: 1 }}
                    placeholder="If (cause), then (event), which may result in (consequence)."
                    helperText="Use the cause, event, consequence format: i.e. 'If paediatric probes are not available (cause), clinicians may be unable to monitor paediatric patients appropriately (event), leading to delayed recognition of deterioration and potential patient harm (consequence).'"
                    autoComplete="off"
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', mt: 0.75 }}
                  >
                    {description || 'No description provided.'}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <SectionLabel>Mitigation</SectionLabel>
                {editMode ? (
                  <TextField
                    value={mitigation}
                    onChange={(e) => setMitigation(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 1 }}
                    autoComplete="off"
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', mt: 0.75 }}
                  >
                    {mitigation || 'No mitigation recorded.'}
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <SectionLabel>Actions</SectionLabel>
                {editMode ? (
                  <TextField
                    value={actions}
                    onChange={(e) => setActions(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    sx={{ mt: 1 }}
                    autoComplete="off"
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line', mt: 0.75 }}
                  >
                    {actions || 'No actions recorded.'}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Right: CQC linkage & owner */}
        <Grid item xs={12} md={5} display="flex">
          <Stack gap={2} sx={{ width: '100%' }}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <FactCheckIcon sx={{ color: '#1E7F88' }} fontSize="small" />
                  <Typography variant="title">CQC Centre</Typography>
                </Stack>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/cqc-centre')}
                >
                  Open
                </Button>
              </Stack>
              <Typography
                variant="smallGrey"
                sx={{ display: 'block', mb: 1.5 }}
              >
                This risk is included in the CQC Centre and the Inspection Pack as
                evidence against the statements below.
              </Typography>

              {editMode ? (
                <CqcLinkageFields
                  activityType={cqcActivityType}
                  onActivityChange={setCqcActivityType}
                  statementKeys={cqcQualityStatementKeys}
                  onStatementKeysChange={setCqcQualityStatementKeys}
                  statements={statements}
                  keyQuestions={keyQuestions}
                  size="small"
                />
              ) : (
                <Stack gap={1.5}>
                  <Box>
                    <SectionLabel>Regulated Activity</SectionLabel>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {CQC_ACTIVITY_LABEL[risk.cqcActivityType] ??
                        'Not related to a regulated activity'}
                    </Typography>
                  </Box>
                  <Box>
                    <SectionLabel>Quality Statements</SectionLabel>
                    {(risk.cqcQualityStatementKeys ?? []).length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Default mapping &mdash; counted under{' '}
                        <strong>Safe</strong> (involving people to manage risks)
                        and <strong>Responsive</strong> (planning for the
                        future).
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.75,
                          mt: 0.75,
                        }}
                      >
                        {risk.cqcQualityStatementKeys.map((key) => (
                          <Chip
                            key={key}
                            size="small"
                            label={`${keyQuestionForStatement(key, statements) ?? ''}: ${statementTitle(key, statements)}`}
                            sx={{ borderRadius: 1, fontWeight: 600 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  {linkedKeyQuestions.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      Feeds the {linkedKeyQuestions.join(', ')} key question
                      {linkedKeyQuestions.length === 1 ? '' : 's'} in your
                      readiness score.
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <SectionLabel>Responsible Manager</SectionLabel>
              <Stack
                direction="row"
                gap={1.5}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <Avatar
                  src={risk.riskOwner?.photograph}
                  sx={{ width: 36, height: 36 }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {risk.riskOwner
                      ? `${risk.riskOwner.firstName} ${risk.riskOwner.lastName}`
                      : 'Not assigned'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Accountable for review and mitigation
                  </Typography>
                </Box>
              </Stack>
              <EmployeeSelector
                label={risk.riskOwner ? 'Reassign to' : 'Assign to'}
                value={null}
                onChange={(e, value) => value && handleAssignOwner(value)}
                fullWidth
                size="small"
                helperText="Saves immediately"
                sx={{ mt: 1.5 }}
              />
            </Paper>
          </Stack>
        </Grid>

        {/* Comments */}
        <Grid item xs={12} display="flex">
          <Paper
            variant="outlined"
            sx={{ p: 2.5, width: '100%' }}
            {...sectionMotion(3)}
          >
            <SectionLabel>Progress</SectionLabel>
            <Typography variant="smallGrey" sx={{ display: 'block', mb: 1.5 }}>
              Posting an update re-scores the risk; leave the toggle on to
              count it as a review.
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmitComment}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'anMuted.main',
              }}
            >
              <TextField
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                size="small"
                multiline
                minRows={2}
                placeholder="What has changed since the last review?"
                required
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                <ConsequenceSelector
                  size="small"
                  label="Current Consequence"
                  value={currentRatingConsequence}
                  onChange={(e) => setCurrentRatingConsequence(e.target.value)}
                />
                <LikelihoodSelector
                  size="small"
                  label="Current Likelihood"
                  value={currentRatingLikelihood}
                  onChange={(e) => setCurrentRatingLikelihood(e.target.value)}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={markReviewed}
                      onChange={(e) => setMarkReviewed(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      Mark as reviewed
                    </Typography>
                  }
                  sx={{ flexShrink: 0, mx: 0, alignSelf: { sm: 'center' } }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  sx={{ px: 4, flexShrink: 0, alignSelf: { sm: 'center' } }}
                >
                  Post
                </Button>
              </Stack>
            </Box>

            <Stack gap={1.5} mt={2}>
              {comments.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No comments yet.
                </Typography>
              )}
              {sortedComments.slice(0, visibleComments).map((comment) => (
                <Stack
                  key={comment._id}
                  direction="row"
                  gap={1.5}
                  alignItems="flex-start"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Avatar
                    src={comment.user?.photograph}
                    sx={{ width: 32, height: 32 }}
                  />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      <strong>
                        {comment.user?.firstName} {comment.user?.lastName}
                      </strong>{' '}
                      &middot;{' '}
                      {moment(comment?.created).format('Do MMM YYYY HH:mm')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {comment.comment}
                    </Typography>
                    {comment.markedAsReviewed && (
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        Marked as reviewed
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}
              {sortedComments.length > visibleComments && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    setVisibleComments((v) => v + COMMENT_PAGE_SIZE)
                  }
                  sx={{ alignSelf: 'center' }}
                >
                  Load more (
                  {sortedComments.length - visibleComments} older)
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  )
}

export default SingleRisk
